import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, type GameDetail } from "../api";
import { useClubs } from "../ClubContext";

function formatTime(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Columns shown on the stats table, in order. Each maps a server event
// `type` string to a display label. Some columns derive from sums (e.g.
// Shots = Shot on Goal + Goal — a Goal is also a shot taken). Toggle
// flags from the game hide unused columns so the table reflects what
// the coach actually tracked.

interface StatCol {
  key: string;
  label: string;
  toggleKey?: keyof GameDetail["game"]["toggles"];
  compute: (entry: GameDetail["rosterEntries"][number]) => number;
}

const STAT_COLS: StatCol[] = [
  { key: "goals", label: "Goals", compute: (e) => e.stats["Goal"] || 0 },
  { key: "shots", label: "Shots", toggleKey: "shots",
    compute: (e) => (e.stats["Shot on Goal"] || 0) + (e.stats["Goal"] || 0) },
  { key: "steals", label: "Steals", toggleKey: "steals", compute: (e) => e.stats["Steal"] || 0 },
  { key: "assists", label: "Assists", toggleKey: "assists", compute: (e) => e.stats["Assist"] || 0 },
  { key: "turnovers", label: "Turnovers", toggleKey: "turnovers", compute: (e) => e.stats["Turnover"] || 0 },
  { key: "blocks", label: "Blocks", toggleKey: "blocks", compute: (e) => e.stats["Block"] || 0 },
  { key: "ejections", label: "Exclusions", compute: (e) => e.ejections },
  { key: "drawn", label: "Ex Drawn", toggleKey: "ejectionsDrawn",
    compute: (e) => e.stats["Ejection Drawn"] || 0 },
  { key: "fives", label: "5M", toggleKey: "fiveM", compute: (e) => e.fiveMs },
];

export function GameDetailPage() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { activeClub, isActiveClubAdmin: isAdmin } = useClubs();
  const [detail, setDetail] = useState<GameDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;
    (async () => {
      try {
        // No clubId needed: the server resolves the game's own club and
        // authorizes against that, answering 404 for anything the caller
        // can't reach.
        const detailRes = await api<GameDetail>(`/v1/games/${gameId}`);
        if (cancelled) return;
        setDetail(detailRes);
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : "Failed to load";
        setError(msg);
        if (msg.toLowerCase().includes("unauthor")) navigate("/login", { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gameId, navigate]);

  if (!detail && !error) {
    return (
      <main className="mx-auto max-w-6xl px-6 py-8">
        <p className="text-sm text-slate-500">Loading…</p>
      </main>
    );
  }
  if (error || !detail) {
    return (
      <main className="mx-auto max-w-6xl px-6 py-8">
        <p className="text-sm text-rose-600">{error ?? "Game not found"}</p>
        <Link to="/" className="mt-4 inline-block text-sm font-medium text-slate-700 hover:underline">
          ← Back to games
        </Link>
      </main>
    );
  }

  const { game, rosterEntries, opponentStats } = detail;
  // Simple mode: no quarters/subs/playing time — hide the quarter length and
  // the per-player Time column (older API responses omit the flag → undefined).
  const simple = game.simpleMode === true;
  const visibleCols = STAT_COLS.filter((c) => !c.toggleKey || game.toggles[c.toggleKey]);
  const sortedEntries = [...rosterEntries].sort((a, b) => {
    if (a.rankOrder !== b.rankOrder) return a.rankOrder - b.rankOrder;
    return a.capNumber - b.capNumber;
  });

  async function handleDelete() {
    if (!gameId) return;
    if (
      !window.confirm(
        "Delete this game for the whole club? This removes it from the cloud for everyone " +
          "(and from the Club list). A coach's local copy on their device is unaffected. This can't be undone."
      )
    )
      return;
    setDeleting(true);
    try {
      await api(`/v1/games/${gameId}`, { method: "DELETE" });
      navigate("/", { replace: true });
    } catch (e) {
      setDeleting(false);
      alert(e instanceof Error ? e.message : "Couldn't delete the game.");
    }
  }

  return (
    <main className="mx-auto max-w-6xl px-6 py-8">
        <Link to="/" className="text-sm font-medium text-slate-600 hover:underline">
          ← Back to games
        </Link>

        <div className="mt-4 rounded-xl border border-slate-200 bg-white p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            {formatDate(game.date)}
            {game.location && <> · {game.location}</>}
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">
            <span className="text-brand-700">{activeClub?.clubName ?? "Home"}</span>{" "}
            vs {game.opponent ?? "TBD"}
          </h1>
          <div className="mt-3 flex items-baseline gap-3">
            <span className="text-3xl font-semibold tabular-nums">
              {game.homeScore}–{game.awayScore}
            </span>
            <span className="text-sm text-slate-500">
              {game.homeScore > game.awayScore ? "Won" : game.homeScore < game.awayScore ? "Lost" : "Tied"}
              {!simple && <> · {game.quarterDurationMins} min quarters</>}
            </span>
          </div>
          {game.recordedBy && (
            <p className="mt-3 text-xs text-slate-500">
              Synced by {game.recordedBy.name ?? game.recordedBy.email ?? "—"}
            </p>
          )}
          {isAdmin && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50"
            >
              {deleting ? "Deleting…" : "Delete game"}
            </button>
          )}
        </div>

        <h2 className="mt-8 mb-3 text-lg font-semibold">Player stats</h2>
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2">Cap</th>
                <th className="px-3 py-2">Player</th>
                <th className="px-3 py-2">Pos</th>
                {!simple && <th className="px-3 py-2 text-right">Time</th>}
                {visibleCols.map((c) => (
                  <th key={c.key} className="px-3 py-2 text-right" title={c.key}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedEntries.map((e) => (
                <tr key={e.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono tabular-nums">{e.capNumber}</td>
                  <td className="whitespace-nowrap px-3 py-2">
                    {e.player.firstName} {e.player.lastName}
                    {e.fouledOut && (
                      <span className="ml-2 rounded bg-rose-100 px-1.5 py-0.5 text-xs font-semibold text-rose-700">
                        FO
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-500">{e.player.position ?? "—"}</td>
                  {!simple && (
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                      {formatTime(e.playTimeSecs)}
                    </td>
                  )}
                  {visibleCols.map((c) => {
                    const v = c.compute(e);
                    return (
                      <td
                        key={c.key}
                        className={`px-3 py-2 text-right tabular-nums ${
                          v === 0 ? "text-slate-300" : "text-slate-900"
                        }`}
                      >
                        {v}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {Object.keys(opponentStats).length > 0 && (
          <>
            <h2 className="mt-8 mb-3 text-lg font-semibold">Opponent</h2>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <dl className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                {Object.entries(opponentStats).map(([type, count]) => (
                  <div key={type} className="flex items-baseline gap-1">
                    <dt className="text-slate-500">{type}</dt>
                    <dd className="font-semibold tabular-nums">{count}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </>
        )}
    </main>
  );
}
